<template>
  <div class="admin-page">
    <main class="form-page">
      <div class="form-page__header">
        <h2 class="form-page__title">FormIQ3 – Baumschutz</h2>
        <p class="form-page__subtitle">
          Kategorien und deren Extraktionsfelder für <strong>{{ CLIENT_ID }}</strong> ({{ WORKFLOW_TYPE }}).
          Änderungen wirken sich direkt auf die SemanticBridge-Konfiguration aus.
        </p>
      </div>

      <div v-if="errorMessage" class="info-box info-box--yellow">
        <span class="info-box__icon">⚠️</span>
        <p>{{ errorMessage }}</p>
      </div>
      <div v-if="statusMessage" class="info-box info-box--green">
        <span class="info-box__icon">✅</span>
        <p>{{ statusMessage }}</p>
      </div>
      <div v-if="loading" class="info-box info-box--blue">
        <span class="info-box__icon">⏳</span>
        <p>Lädt Konfiguration…</p>
      </div>

      <template v-if="workflow">
        <div class="finder">
          <div class="finder__sidebar">
            <button
              v-for="type in categories"
              :key="type"
              type="button"
              class="finder__item"
              :class="{ 'finder__item--active': type === selectedType }"
              @click="selectCategory(type)"
            >
              <span class="finder__item-label">{{ type }}</span>
              <span class="finder__item-remove" title="Kategorie entfernen" @click.stop="removeCategoryHandler(type)">✕</span>
            </button>

            <div class="finder__add-row">
              <input
                class="field__input finder__add-input"
                type="text"
                v-model="newCategoryId"
                placeholder="NEUE_KATEGORIE"
                @keyup.enter="addNewCategory"
              />
              <button type="button" class="add-field-btn" @click="addNewCategory">+</button>
            </div>
          </div>

          <div class="finder__detail">
            <template v-if="selectedType">
              <div class="field finder__rename">
                <label class="field__label">Kategorie-ID</label>
                <input
                  class="field__input"
                  type="text"
                  v-model="renamingValue"
                  @blur="applyRename"
                  @keyup.enter="applyRename"
                />
              </div>

              <div class="section-title">Extraktionsfelder (Kategorie-Merkmal / Beschreibung)</div>
              <DynamicFieldList :modelValue="fields" @update:modelValue="onFieldsChange" />
            </template>
            <p v-else class="finder__empty">Keine Kategorie ausgewählt.</p>
          </div>
        </div>

        <button class="primary-btn" :disabled="saving" @click="save">
          {{ saving ? 'Speichert…' : 'Änderungen speichern' }}
        </button>
      </template>
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { workflowApi } from '../services/workflowApi.js';
import DynamicFieldList from '../components/DynamicFieldList.vue';
import {
  getCategories,
  getFieldsForCategory,
  setFieldsForCategory,
  addCategory,
  removeCategory,
  renameCategory,
} from '../utils/categoryExtraction.js';

const CLIENT_ID = 'form3_baumschutz';
const WORKFLOW_TYPE = 'DOCUMENT_CATEGORISE';

export default {
  name: 'AdminFormIQ3Page',
  components: { DynamicFieldList },
  setup() {
    const loading = ref(false);
    const saving = ref(false);
    const errorMessage = ref('');
    const statusMessage = ref('');

    const workflow = ref(null);
    const selectedType = ref(null);
    const renamingValue = ref('');
    const fields = ref([]);
    const newCategoryId = ref('');

    const categories = computed(() => (workflow.value ? getCategories(workflow.value.configuration) : []));

    function selectCategory(type) {
      selectedType.value = type;
      renamingValue.value = type || '';
      fields.value = type && workflow.value ? getFieldsForCategory(workflow.value.configuration, type) : [];
    }

    function onFieldsChange(newFields) {
      fields.value = newFields;
      if (workflow.value && selectedType.value) {
        setFieldsForCategory(workflow.value.configuration, selectedType.value, newFields);
      }
    }

    function addNewCategory() {
      const id = newCategoryId.value.trim();
      if (!id || !workflow.value) return;
      addCategory(workflow.value, id);
      newCategoryId.value = '';
      selectCategory(id);
    }

    function removeCategoryHandler(type) {
      if (!workflow.value) return;
      removeCategory(workflow.value, type);
      if (selectedType.value === type) {
        selectCategory(categories.value[0] || null);
      }
    }

    function applyRename() {
      const newVal = renamingValue.value.trim();
      if (!workflow.value || !selectedType.value || !newVal || newVal === selectedType.value) return;
      renameCategory(workflow.value, selectedType.value, newVal);
      selectedType.value = newVal;
    }

    async function load() {
      loading.value = true;
      errorMessage.value = '';
      statusMessage.value = '';
      try {
        const result = await workflowApi.getWorkflow(CLIENT_ID, WORKFLOW_TYPE);
        if (!result) {
          errorMessage.value = `Keine ${WORKFLOW_TYPE}-Konfiguration für ${CLIENT_ID} gefunden.`;
          workflow.value = null;
          return;
        }
        workflow.value = result;
        selectCategory(getCategories(result.configuration)[0] || null);
      } catch (err) {
        errorMessage.value = err.message;
      } finally {
        loading.value = false;
      }
    }

    async function save() {
      if (!workflow.value) return;
      saving.value = true;
      errorMessage.value = '';
      statusMessage.value = '';
      try {
        const payload = {
          client_id: workflow.value.client_id,
          workflow_type: workflow.value.workflow_type,
          domain: workflow.value.domain,
          meta_data: workflow.value.meta_data,
          configuration: workflow.value.configuration,
        };
        const updated = await workflowApi.updateWorkflow(payload);
        workflow.value = updated;
        const stillExists = selectedType.value && getCategories(updated.configuration).includes(selectedType.value);
        selectCategory(stillExists ? selectedType.value : getCategories(updated.configuration)[0] || null);
        statusMessage.value = 'Änderungen gespeichert.';
      } catch (err) {
        errorMessage.value = err.message;
      } finally {
        saving.value = false;
      }
    }

    onMounted(load);

    return {
      CLIENT_ID,
      WORKFLOW_TYPE,
      loading,
      saving,
      errorMessage,
      statusMessage,
      workflow,
      categories,
      selectedType,
      renamingValue,
      fields,
      newCategoryId,
      selectCategory,
      onFieldsChange,
      addNewCategory,
      removeCategoryHandler,
      applyRename,
      save,
    };
  },
};
</script>

<style scoped src="../forms/form.css"></style>
<style scoped>
.admin-page {
  background: #f0f2f8;
}

/* form.css's .form-page is tuned for the narrow citizen-facing application
   forms it's shared with (see ApplicationViewC.vue) - the admin page needs
   the full content width instead. */
.form-page {
  max-width: none;
  margin: 0;
}

.finder {
  display: flex;
  gap: 16px;
  min-height: 360px;
  margin-bottom: 20px;
}

.finder__sidebar {
  flex: 0 0 220px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #fff;
  border: 1px solid #d0d7de;
  border-radius: 4px;
  padding: 10px;
}

.finder__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 3px;
  border: none;
  background: transparent;
  text-align: left;
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.finder__item:hover {
  background: #eef4fb;
}

.finder__item--active {
  background: #003366;
  color: #fff;
}

.finder__item-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.finder__item-remove {
  flex-shrink: 0;
  opacity: 0.6;
  font-size: 12px;
}

.finder__item-remove:hover {
  opacity: 1;
  color: #c0121c;
}

.finder__add-row {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #eef0f2;
}

.finder__add-input {
  flex: 1;
  font-size: 12px;
  padding: 7px 10px;
}

.finder__detail {
  flex: 1;
  background: #fff;
  border: 1px solid #d0d7de;
  border-radius: 4px;
  padding: 18px 22px;
}

.finder__rename {
  max-width: 320px;
  margin-bottom: 20px;
}

.finder__empty {
  color: #888;
  font-size: 13px;
}

.add-field-btn {
  background: #eef4fb;
  border: 1px solid #c3d9f0;
  color: #1e6eb5;
  padding: 0 14px;
  border-radius: 3px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.add-field-btn:hover {
  background: #dbe9f7;
}

.primary-btn {
  background: #003366;
  color: #fff;
  border: none;
  padding: 10px 24px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
}

.primary-btn:hover {
  background: #002a52;
}

.primary-btn:disabled {
  background: #adb5bd;
  cursor: not-allowed;
}
</style>
